// Les deux sites sources changent de nom de domaine sans prévenir. Leurs URL ne
// sont donc pas figées dans le bundle : le serveur les lit dans son `.env` et
// les sert sur `/config`, que `loadConfig` récupère au démarrage. Tant qu'il
// n'a pas répondu — ou s'il ne répond pas — ce sont ces valeurs qui servent.
let animesama = "https://anime-sama.to";
let voiranime = "https://voir-anime.to";

export function animesamaBase(): string {
  return animesama;
}

export function voiranimeBase(): string {
  return voiranime;
}

export const SIBNET_BASE = "https://video.sibnet.ru";

/** Ne retient qu'une URL http(s) exploitable, sans `/` final : tout le code
 *  ajoute le sien. Le reste laisse la valeur en place. */
function cleanBase(value: unknown, current: string): string {
  if (typeof value !== "string") return current;

  try {
    const url = new URL(value.trim());

    return url.protocol === "http:" || url.protocol === "https:"
      ? url.origin
      : current;
  } catch {
    return current;
  }
}

/** À appeler avant la première requête vers une source. Un échec n'est jamais
 *  bloquant : on garde les domaines par défaut. */
export async function loadConfig(): Promise<void> {
  try {
    const res = await fetch("/config");
    if (!res.ok) return;

    const body = (await res.json()) as { animesama?: unknown; voiranime?: unknown };

    animesama = cleanBase(body.animesama, animesama);
    voiranime = cleanBase(body.voiranime, voiranime);
  } catch {
    // serveur plus ancien, hors ligne, ou réponse illisible
  }
}

/** Miroirs interchangeables : ils servent les mêmes ID d'embed mais tous ne
 *  répondent pas en permanence. Ordre = ordre d'essai après l'URL du site. */
export const EMBED_MIRROR_HOSTS = ["ansembed.net", "voembed.net"];

/** Domaines vidmoly historiques : plus aucune résolution DNS pour .biz et .net,
 *  et .to ne renvoie plus le lecteur. */
export const DEAD_EMBED_HOSTS = ["vidmoly.biz", "vidmoly.net", "vidmoly.to"];

export type Source = "animesama" | "voiranime";

export function sourceReferer(source: Source): string {
  return (source === "animesama" ? animesama : voiranime) + "/";
}

/** Réécrit les anciens domaines vidmoly vers ansembed.net : vidmoly.biz et
 *  vidmoly.net ne résolvent plus, vidmoly.to ne sert plus que de la publicité.
 *  Les ID d'embed, eux, restent valides sur les hôtes vivants. */
export function normalizeEmbedHost(url: string): string {
  let out = url;

  for (const host of DEAD_EMBED_HOSTS) {
    out = out.replaceAll(host, EMBED_MIRROR_HOSTS[0]!);
  }

  return out;
}

/** Déduit le Referer attendu par l'hôte du lecteur. */
export function embedReferer(embedUrl: string): string {
  try {
    const url = new URL(embedUrl);
    return `${url.protocol}//${url.host.toLowerCase()}/`;
  } catch {
    return `https://${EMBED_MIRROR_HOSTS[0]}/`;
  }
}

/** Retourne l'URL demandée puis ses équivalents sur les autres domaines
 *  miroirs, en conservant l'ordre d'essai. */
export function embedMirrors(embedUrl: string): string[] {
  const candidates = [embedUrl];

  let url: URL;

  try {
    url = new URL(embedUrl);
  } catch {
    return candidates;
  }

  if (!EMBED_MIRROR_HOSTS.includes(url.hostname.toLowerCase())) return candidates;

  for (const host of EMBED_MIRROR_HOSTS) {
    if (host === url.hostname.toLowerCase()) continue;

    const mirror = new URL(url.toString());
    mirror.hostname = host;
    candidates.push(mirror.toString());
  }

  return candidates;
}

/** Résout une URL éventuellement relative contre la page qui la porte
 *  (certains lecteurs ne servent que des chemins absolus sans hôte). */
export function resolveRelative(base: string, ref: string): string {
  try {
    return new URL(ref, base).toString();
  } catch {
    return ref;
  }
}
